import { useEffect } from 'react';

import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { parseAudioShareLink } from '../../common/audioSharingUtils';
import type { ActiveContent } from '../../types/activeContent';

import { useAudioPlayerStore } from './useAudioPlayerStore';

/**
 * Hook that detects and handles audio share links from URL parameters on initial page load.
 * When an ?audioShare parameter is present, it parses the link, updates the current
 * activeContent with the supporting content from the share link, sets the active feature,
 * and opens the audio modal.
 *
 * This hook runs on mount and when rerenders happen with different query params.
 *
 * @param activeContent - The active content to open if audio sharing link was detected.
 * @param isUninitialized - Whether or not the audio player is uninitialized.
 * @param isDisabled - Whether or not the hook is disabled (activeContent is not ready or audio is disabled).
 */
export const useAudioShareLink = (
	activeContent: ActiveContent,
	isUninitialized: boolean,
	isDisabled?: boolean,
): void => {
	const { setActiveFeature, openAudioPlayer } = useAudioPlayerStore();
	const { sendUIEvent } = usePlatformInstrumentation();
	const currentSearch = window.location.search;

	useEffect(() => {
		if (isDisabled) {
			return;
		}

		const audioShareParam = new URLSearchParams(currentSearch).get('audioShare');
		if (!audioShareParam) {
			return;
		}

		const { feature, supportingContent } = parseAudioShareLink(window.location.href);
		setActiveFeature(feature);
		openAudioPlayer({ ...activeContent, supportingContent }, { isUninitialized });
		sendUIEvent({
			eventType: 'ui',
			action: 'opened',
			actionSubject: 'aiAudioShareLink',
			attributes: {
				contentType: activeContent.contentType,
				trackType: feature,
			},
		});

		const url = new URL(window.location.href);
		url.searchParams.delete('audioShare');
		window.history.replaceState({}, '', url.toString());
	}, [
		setActiveFeature,
		openAudioPlayer,
		sendUIEvent,
		activeContent,
		isDisabled,
		isUninitialized,
		currentSearch,
	]);
};
